import { env } from "@diana/env-ui";

export interface EndpointConfiguration {
  buildEnvironment: string;
  isCloud: boolean;
  sec: string;
  loginEndpoint: string;
  requestNewOtpEndpoint: string;
  defaultReturnUrl: string;
}

export interface EdgeEndpointConfiguration extends EndpointConfiguration {
  api: string;
  hub: string;
  hubAnonymous: string;
  baseUrl: string;
}

export interface CloudEndpointConfiguration extends EndpointConfiguration {}

const getEdgeEndpoints = (): Omit<
  EdgeEndpointConfiguration,
  "buildEnvironment" | "buildType" | "baseUrl" | "isCloud" | "defaultReturnUrl"
> => {
  const baseOrigin = `${window.location.protocol}//${window.location.hostname}`;

  const apiUrl = `${baseOrigin}:${env("API_PORT")}`;
  const secUrl = `${baseOrigin}:${env("SEC_PORT")}`;
  const hubUrl = `${apiUrl}${env("HUB_PATH")}`;
  const hubAnonymousUrl = `${apiUrl}${env("HUB_ANONYMOUS_PATH")}`;

  const loginEndpoint = new URL("/account/login", secUrl).href;
  const requestNewOtpEndpoint = new URL("/api/v1/edge/otp/requestNew", apiUrl)
    .href;

  const urlConfig = {
    api: apiUrl,
    sec: secUrl,
    hub: hubUrl,
    hubAnonymous: hubAnonymousUrl,
    loginEndpoint,
    requestNewOtpEndpoint,
  };

  return urlConfig;
};

const getCloudEndpoints = (): Omit<
  CloudEndpointConfiguration,
  "buildEnvironment" | "isCloud" | "defaultReturnUrl"
> => {
  const secUrl = env("DIANA_SEC");

  const loginEndpoint = new URL("/account/otp", secUrl).href;
  const requestNewOtpEndpoint = new URL("/account/otpReset", secUrl).href;

  const urlConfig = {
    sec: secUrl,
    loginEndpoint,
    requestNewOtpEndpoint,
  };

  return urlConfig;
};

const createEndpointConfiguration = (): EdgeEndpointConfiguration | CloudEndpointConfiguration => {
  const buildType = env("TYPE");
  const isCloud = buildType.toLowerCase() === "cloud";

  const endpoints = isCloud ? getCloudEndpoints() : getEdgeEndpoints();

  const buildEnvironment = getBuildEnv();
  const defaultReturnUrl = getDefaultReturnUrl(buildEnvironment);

  const endpointConfig = {
    buildEnvironment,
    defaultReturnUrl,
    isCloud,
    baseUrl: endpoints.sec,
    ...endpoints,
  };

  return endpointConfig;
}

const buildEnvLookup : Record<string, string> = {
  "Production": "prod",
  "Test": "test",
  "Dev": "dev",
  "Staging": "staging"
};

const getBuildEnv = () : string => {
  const fullBuildEnv = env("ENV");
  const buildEnv = buildEnvLookup[fullBuildEnv] ?? buildEnvLookup["Dev"]; 

  return buildEnv;
};

const envUrlPrefixes : Record<string, string> = {
  "prod": "app",
  "test": "test",
  "dev": "dev",
  "staging": "staging"
};

const getDefaultReturnUrl = (buildEnvironment: string) : string => {
  const defaultReturnUrlSubdomain = envUrlPrefixes[buildEnvironment] ?? envUrlPrefixes["dev"];

  const isAdpLogin = true;
  const defaultReturnUrlDomain =
    isAdpLogin ?
      buildEnvironment === "dev" ? "idiana.io" :
      buildEnvironment === "staging" ? "rise-x.com" :
      "adpclear.io" :
    "idiana.io";
  const defaultReturnUrlEnvironment = isAdpLogin ? "adpclear": "";
  const defaultReturnUrl = `https://${defaultReturnUrlSubdomain}.${defaultReturnUrlDomain}/${defaultReturnUrlEnvironment}`;

  return defaultReturnUrl;
};

const endpointConfig = createEndpointConfiguration();

export const getEndpointConfiguration = () => endpointConfig;